import { handleError } from '@slr/shared-library';
import { API_USER_SERVICE_URL } from 'configs';
import { useCallback } from 'react';
import useGetApiConfig from '../useGetApiConfig';
import { PlatformAdminUserPage } from './platformAdminUser.types';

const buildQuery = (
  parameters: Record<string, string | number | boolean | undefined>
) => {
  const searchParams = new URLSearchParams();
  Object.entries(parameters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.set(key, String(value));
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

export const usePlatformAdminUsersApi = () => {
  const apiConfig = useGetApiConfig(API_USER_SERVICE_URL);

  const authorizedFetch = useCallback(
    async (path: string, init?: RequestInit) => {
      const accessToken =
        typeof apiConfig.accessToken === 'function'
          ? await apiConfig.accessToken()
          : apiConfig.accessToken;
      const response = await fetch(`${apiConfig.basePath}${path}`, {
        ...init,
        headers: {
          Accept: 'application/json',
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...(accessToken ? { Authorization: accessToken } : {}),
          ...init?.headers
        }
      });
      if (!response.ok) {
        let message = response.statusText;
        let uniqueExceptionType: string | undefined;
        try {
          const errorBody = await response.json();
          message = errorBody?.message || errorBody?.detail || message;
          uniqueExceptionType =
            typeof errorBody?.uniqueExceptionType === 'string'
              ? errorBody.uniqueExceptionType
              : undefined;
        } catch {}
        const error = new Error(message) as Error & {
          status?: number;
          uniqueExceptionType?: string;
        };
        error.status = response.status;
        error.uniqueExceptionType = uniqueExceptionType;
        throw error;
      }
      if (response.status === 204) {
        return undefined;
      }
      return response.json();
    },
    [apiConfig]
  );

  const searchUsers = useCallback(
    (
      search: string | undefined,
      page: number,
      count: number,
      platformAdministrator?: boolean
    ) =>
      authorizedFetch(
        `/admin/users${buildQuery({
          search,
          page,
          count,
          platformAdministrator
        })}`
      )
        .then((data) => data as PlatformAdminUserPage)
        .catch(handleError),
    [authorizedFetch]
  );

  const setUserEnabled = useCallback(
    (userId: string, enabled: boolean) =>
      authorizedFetch(`/admin/users/${encodeURIComponent(userId)}/enabled`, {
        method: 'PUT',
        body: JSON.stringify({ enabled })
      }).catch(handleError),
    [authorizedFetch]
  );

  const setPlatformAdministrator = useCallback(
    (userId: string, granted: boolean) =>
      authorizedFetch(
        `/admin/users/${encodeURIComponent(userId)}/platform-administrator`,
        {
          method: 'PUT',
          body: JSON.stringify({ granted })
        }
      ).catch(handleError),
    [authorizedFetch]
  );

  return {
    searchUsers,
    setUserEnabled,
    setPlatformAdministrator
  };
};
